import { takeEvery, select, put } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IResponseError } from 'ApiPackage/MainModule/interfaces/IResponseError';
import { IFilterValue } from 'ApiPackage/SearchModule/interfaces/IFilterValue';
import { ISearchResponse } from 'ApiPackage/SearchModule/interfaces/ISearchResponse';
import {
  getSearchPasport,
  exportInOds,
  exportInXlsx,
  fetchFilterValues,
} from 'ApiPackage/SearchModule/services/search';
import {
  captchaSetIsActive,
  captchaSetActionAfterTokenLoad,
  captchaSetError,
} from 'CorePackage/CaptchaModule/actions/captchaActions';
import { getCaptchaToken } from 'CorePackage/CaptchaModule/selectors/captchaSelectors';
import { coreShowNotice } from 'CorePackage/MainModule/actions/coreActions';
import { INTERNAL_SERVER_ERROR } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/MainModule/enums/NoticeTypesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { FilterOptions } from '../types/FilterOptions';
import { SearchFiltersOptionTypesEnum } from '../enums/SearchFiltersOptionTypesEnum';
import { SearchActionsEnum } from '../enums/SearchActionsEnum';
import { ISearchFilterData } from '../interfaces/ISearchFilterData';
import { EXPORT_CHECK_LIST_XLSX_FILE_NAME, EXPORT_CHECK_LIST_ODS_FILE_NAME } from '../constants/searchExportFileNames';
import {
  searchSetResultData,
  searchGetResultData,
  searchGetResultDataNextPage,
  searchExportData,
  searchSetFilterOptions,
} from '../actions/searchActions';
import { getSearchFilterData } from '../common/getSearchFilterData';
import {
  getSearchOrder,
  getSearchFilter,
  getSearchString,
  getSearchPage,
  getSearchResultsData,
} from '../selectors/searchSelectors';
import { SearchFiltersOptionTypesEnumByNumber } from '../enums/SearchFiltersOptionTypesEnumByNumber';

const createSearchParams = (order: string, page: number, filter: Partial<ISearchFilterData>, searchString?: string) => {
  const params = new URLSearchParams();
  params.append('page', `${String(50)},${String(page)}`);
  Object.keys(filter).forEach(item => {
    const value = (filter as unknown as Record<string, string | []>)[item];
    if (value) {
      if (typeof value === 'string') {
        params.set(item, value);
      } else {
        value.forEach(v => params.append(item, v));
      }
    }
  });
  if (searchString && searchString !== '') {
    params.set('searchString', searchString);
  }
  if (order) {
    params.append('order', order);
  }
  return params;
};

function* searchGetResultDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getSearchOrder(state);
    const filterValues = getSearchFilter(state);
    const searchString = getSearchString(state);
    const filter = getSearchFilterData(filterValues);
    const searchParams = createSearchParams(order, 1, filter, searchString);
    const captchaToken = getCaptchaToken(state);
    if (
      !searchString &&
      filter &&
      !Object.values(filter).find((item: unknown & { length?: number }) => item?.length && item?.length > 0)
    ) {
      return;
    }
    const result: ISearchResponse = yield getSearchPasport(searchParams.toString(), captchaToken);
    yield put(searchSetResultData(result));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const { status } = err;
      if (status === 403) {
        yield put(captchaSetIsActive(true));
        yield put(captchaSetActionAfterTokenLoad(searchGetResultData));
      } else if (status === 500) {
        yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
      } else {
        yield put(captchaSetError('Неверное проверочное значение'));
        yield put(captchaSetIsActive(true));
      }
    },
  });
}

function* searchGetResultDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getSearchOrder(state);
    const filterValues = getSearchFilter(state);
    const searchString = getSearchString(state);
    const page = getSearchPage(state);
    const resultsData = getSearchResultsData(state);
    const filter = getSearchFilterData(filterValues);
    const searchParams = createSearchParams(order, page + 1, filter, searchString);
    const captchaToken = getCaptchaToken(state);
    const result: ISearchResponse = yield getSearchPasport(searchParams.toString(), captchaToken);
    const passportData = resultsData ? [...resultsData, ...result.content] : [...result.content];
    yield put(searchSetResultData({ ...result, content: passportData }));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const { status } = err;
      if (status === 403) {
        yield put(captchaSetIsActive(true));
        yield put(captchaSetActionAfterTokenLoad(searchGetResultDataNextPage));
      } else if (status === 500) {
        yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
      } else {
        yield put(captchaSetError('Неверное проверочное значение'));
        yield put(captchaSetIsActive(true));
      }
    },
  });
}

type ExportDataType = () => {
  type: 'SEARCH_EXPORT_DATA';
  payload: {
    exportFn: (params: string, captchaToken: string | null) => Promise<Blob>;
    checkFilename: string;
  };
};

function* searchExportDataSaga(action: ActionType<ExportDataType>) {
  const { exportFn, checkFilename } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const order = getSearchOrder(state);
    const filterValues = getSearchFilter(state);
    const searchString = getSearchString(state);
    const page = getSearchPage(state);
    const filter = getSearchFilterData(filterValues);
    const searchParams = createSearchParams(order, page, filter, searchString);
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    const captchaToken = getCaptchaToken(state);
    const response: Blob = yield exportFn(params.toString(), captchaToken);
    const blob = new Blob([response]);
    saveFile(blob, checkFilename);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const { status } = err;
      if (status === 403) {
        yield put(captchaSetIsActive(true));
        if (checkFilename === EXPORT_CHECK_LIST_ODS_FILE_NAME) {
          yield put(captchaSetActionAfterTokenLoad(searchExportData(exportInOds, EXPORT_CHECK_LIST_ODS_FILE_NAME)));
        } else {
          yield put(captchaSetActionAfterTokenLoad(searchExportData(exportInXlsx, EXPORT_CHECK_LIST_XLSX_FILE_NAME)));
        }
      } else if (status === 500) {
        yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
      } else {
        yield put(
          coreShowNotice(
            status === 504 ? 'Ошибка 504. Удаленный сервер не отвечает' : 'Ошибка на сервере',
            NoticeTypesEnum.ERROR,
          ),
        );
      }
    },
  });
}

function* searchGetFilterOptionsSaga() {
  function* funcTry() {
    const data: IFilterValue[] = yield fetchFilterValues();
    const filtredOption = data
      .map(item => {
        return item.attribute.map((attr, attrIndex) => {
          if (item.id === SearchFiltersOptionTypesEnumByNumber.MONTH_KNM + 1) {
            return { value: attrIndex + 1, label: attr.name || '', id: attr.id || 0 };
          }
          return { value: attr.name, label: attr.name || '', id: attr.id || 0 };
        });
      })
      .reduce((acc: FilterOptions | undefined, item, index) => {
        if (!item.length) {
          return acc;
        }
        return {
          ...acc,
          [Object.values(SearchFiltersOptionTypesEnum)[index]]: {
            data: item,
          },
        };
      }, undefined);
    yield put(searchSetFilterOptions(filtredOption));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(coreShowNotice(INTERNAL_SERVER_ERROR, NoticeTypesEnum.ERROR));
    },
  });
}

export function* watchSearchSaga() {
  yield takeEvery(SearchActionsEnum.SEARCH_GET_RESULT_DATA, searchGetResultDataSaga);
  yield takeEvery(SearchActionsEnum.SEARCH_GET_RESULT_DATA_NEXT_PAGE, searchGetResultDataNextPageSaga);
  yield takeEvery(SearchActionsEnum.SEARCH_EXPORT_DATA, searchExportDataSaga);
  yield takeEvery(SearchActionsEnum.SEARCH_GET_FILTER_OPTIONS, searchGetFilterOptionsSaga);
}
